import { useTranslate } from "ra-core";
import type { ReactElement } from "react";

import { ChoiceFilter } from "../filters/ChoiceFilter";
import { useStarChoices } from "../filters/commonFilters";
import { TextFilter } from "../filters/TextFilter";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { CustomFieldDefinition, CustomFieldResource } from "../types";

/** PostgREST JSON path to a custom field, as text (->>) or as JSON (->). */
const asText = (field: CustomFieldDefinition) => `custom_fields->>${field.key}`;
const asJson = (field: CustomFieldDefinition) => `custom_fields->${field.key}`;

/**
 * Top-bar filters for the custom fields created in the Settings: choices for
 * lists, yes / no and ratings, a « contains » search for text. Numbers,
 * amounts and dates have no filter yet.
 */
export const useCustomFieldFilterInputs = (
  resource: CustomFieldResource,
): ReactElement[] => {
  const translate = useTranslate();
  const { customFields } = useConfigurationContext();
  const starChoices = useStarChoices();
  const booleanChoices = [
    { value: "true", label: translate("ra.boolean.true") },
    { value: "false", label: translate("ra.boolean.false") },
  ];

  return (customFields?.[resource] ?? []).flatMap((field): ReactElement[] => {
    const options = field.options ?? [];
    switch (field.type) {
      case "select":
        return [
          <ChoiceFilter
            key={field.key}
            source={`${asText(field)}@in`}
            label={field.label}
            choices={options}
          />,
        ];
      case "multi_select":
        return [
          <ChoiceFilter
            key={field.key}
            source={`${asJson(field)}@cs`}
            label={field.label}
            choices={options}
            mode="json"
          />,
        ];
      case "boolean":
        return [
          <ChoiceFilter
            key={field.key}
            source={`${asText(field)}@eq`}
            label={field.label}
            choices={booleanChoices}
            mode="single"
          />,
        ];
      case "rating":
        return [
          <ChoiceFilter
            key={field.key}
            source={`${asText(field)}@in`}
            label={field.label}
            choices={starChoices}
          />,
        ];
      case "text":
      case "long_text":
      case "url":
        return [
          <TextFilter
            key={field.key}
            source={`${asText(field)}@ilike`}
            label={field.label}
          />,
        ];
      default:
        return [];
    }
  });
};
